import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from '@material-ui/core';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import GppGoodRoundedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { publicSignatureApi } from '../api/publicSignatureApi';

const ACCENT = '#3b82f6';

// Déclenche le téléchargement d'un PDF encodé en base64.
const downloadBase64Pdf = (base64, filename) => {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) arr[i] = bytes.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([arr], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const AgreementSignPublic = () => {
  const { token } = useParams();
  // Tenant transmis dans le lien (?tenant=...) ; repli sur le sous-domaine.
  const tenant = new URLSearchParams(window.location.search).get('tenant') || window.location.hostname.split('.')[0];

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [alreadySigned, setAlreadySigned] = useState(false);

  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasSig, setHasSig] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await publicSignatureApi.getSignInfo(token, tenant);
      if (res.status === 200) {
        if (res.data.already_signed) {
          setAlreadySigned(true);
        } else {
          setInfo(res.data);
          setEmail(res.data.signer_email || '');
        }
      } else {
        setError(res.message || 'Lien invalide.');
      }
    } catch (e) {
      setError('Impossible de charger la convention.');
    } finally {
      setLoading(false);
    }
  }, [token, tenant]);

  useEffect(() => { load(); }, [load]);

  // Init du contexte de dessin une fois le canvas monté.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
  }, [info]);

  const point = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (cx - r.left) * (c.width / r.width), y: (cy - r.top) * (c.height / r.height) };
  };
  const startDraw = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const p = point(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const moveDraw = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const p = point(e); ctx.lineTo(p.x, p.y); ctx.stroke(); if (!hasSig) setHasSig(true); };
  const endDraw = () => { drawing.current = false; };
  const clearSig = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); setHasSig(false); };

  const handleOtp = async () => {
    const res = await publicSignatureApi.requestOtp(token, tenant);
    if (res.status === 200) setOtpRequested(true);
    else setError(res.message || 'Envoi du code impossible.');
  };

  const handleSign = async () => {
    setError('');
    setSubmitting(true);
    try {
      const signatureImage = canvasRef.current.toDataURL('image/png');
      const res = await publicSignatureApi.sign(token, {
        email,
        consent: true,
        signature_image: signatureImage,
        otp: otp || undefined,
      }, tenant);
      if (res.status === 200) {
        setDone(true);
      } else {
        setError(res.message || 'La signature a échoué.');
      }
    } catch (e) {
      setError("Une erreur s'est produite.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async () => {
    const res = await publicSignatureApi.downloadSigned(token, tenant);
    if (res.status === 200 && res.data.pdf_base64) {
      downloadBase64Pdf(res.data.pdf_base64, 'convention-signee.pdf');
    }
  };

  const pageWrap = (children) => (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', py: { xs: 2, md: 6 }, px: 2 }}>
      <Box sx={{ width: '100%', maxWidth: 860 }}>{children}</Box>
    </Box>
  );

  if (loading) {
    return pageWrap(<Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>);
  }

  if (error && !info && !alreadySigned) {
    return pageWrap(
      <Card sx={{ p: 5, borderRadius: '20px', textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#dc2626' }}>Lien indisponible</Typography>
        <Typography sx={{ mt: 1, color: 'text.secondary' }}>{error}</Typography>
      </Card>
    );
  }

  if (alreadySigned || done) {
    return pageWrap(
      <Card sx={{ p: 5, borderRadius: '20px', textAlign: 'center' }}>
        <CheckCircleRoundedIcon sx={{ fontSize: 56, color: '#16a34a' }} />
        <Typography sx={{ mt: 1.5, fontWeight: 800, fontSize: '1.3rem', color: 'text.primary' }}>Convention signée</Typography>
        <Typography sx={{ mt: 1, color: 'text.secondary' }}>
          Votre convention de formation a bien été signée électroniquement. Une copie vous a été envoyée par email.
        </Typography>
        <Button onClick={handleDownload} variant="contained" sx={{ mt: 3, borderRadius: '10px', textTransform: 'none', fontWeight: 700, backgroundColor: ACCENT }}>
          Télécharger ma convention signée
        </Button>
      </Card>
    );
  }

  const canSign = consent && hasSig && email.trim() && (!info.require_otp || otp.trim());

  return pageWrap(
    <Card sx={{ borderRadius: '22px', overflow: 'hidden', boxShadow: '0 12px 40px rgba(15,23,42,0.08)' }}>
      {/* En-tête */}
      <Box sx={{ p: { xs: 2.5, md: 3.5 }, background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)', color: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <GppGoodRoundedIcon />
          <Typography sx={{ fontWeight: 800, fontSize: '1.2rem' }}>Signature de votre convention</Typography>
        </Box>
        <Typography sx={{ mt: 0.5, fontSize: '0.85rem', opacity: 0.9 }}>{info.formation_title}</Typography>
      </Box>

      <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
        {/* Aperçu PDF */}
        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', mb: 1, color: 'text.primary' }}>Document à signer</Typography>
        {info.pdf_base64 ? (
          <Box
            component="iframe"
            title="Convention"
            src={`data:application/pdf;base64,${info.pdf_base64}`}
            sx={{ width: '100%', height: 420, border: '1px solid #e2e8f0', borderRadius: '12px' }}
          />
        ) : (
          <Typography sx={{ color: 'text.disabled', fontStyle: 'italic' }}>Aperçu indisponible.</Typography>
        )}

        {/* Identité */}
        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', mt: 3, mb: 1, color: 'text.primary' }}>Vos informations</Typography>
        <TextField
          fullWidth size="small" label="Votre adresse email" value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
        />

        {/* OTP optionnel */}
        {info.require_otp && (
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, alignItems: 'center' }}>
            <Button onClick={handleOtp} variant="outlined" sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
              {otpRequested ? 'Renvoyer le code' : 'Recevoir un code'}
            </Button>
            {otpRequested && (
              <TextField size="small" label="Code reçu par email" value={otp} onChange={(e) => setOtp(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            )}
          </Box>
        )}

        {/* Signature */}
        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', mt: 3, mb: 1, color: 'text.primary' }}>Votre signature</Typography>
        <Box sx={{ border: '1px dashed #94a3b8', borderRadius: '12px', backgroundColor: '#fff', position: 'relative' }}>
          <Box
            component="canvas"
            ref={canvasRef}
            width={760}
            height={180}
            onMouseDown={startDraw}
            onMouseMove={moveDraw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={moveDraw}
            onTouchEnd={endDraw}
            sx={{ width: '100%', height: 180, touchAction: 'none', display: 'block', cursor: 'crosshair' }}
          />
          {!hasSig && (
            <Typography sx={{ position: 'absolute', top: '50%', left: 0, right: 0, transform: 'translateY(-50%)', textAlign: 'center', color: '#cbd5e1', pointerEvents: 'none', fontStyle: 'italic' }}>
              Signez ici avec la souris ou le doigt
            </Typography>
          )}
        </Box>
        <Button onClick={clearSig} sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.78rem', color: 'text.secondary' }}>Effacer</Button>

        {/* Consentement */}
        <FormControlLabel
          sx={{ mt: 1, alignItems: 'flex-start' }}
          control={<Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} sx={{ pt: 0 }} />}
          label={<Typography sx={{ fontSize: '0.84rem', color: 'text.secondary' }}>{info.consent_text}</Typography>}
        />

        {error && <Typography sx={{ mt: 1, color: '#dc2626', fontSize: '0.85rem' }}>{error}</Typography>}

        <Button
          onClick={handleSign}
          disabled={!canSign || submitting}
          variant="contained"
          fullWidth
          sx={{ mt: 2.5, py: 1.2, borderRadius: '12px', textTransform: 'none', fontWeight: 800, fontSize: '0.95rem', backgroundColor: ACCENT, '&:hover': { backgroundColor: '#2563eb' } }}
        >
          {submitting ? 'Signature en cours…' : 'Signer la convention'}
        </Button>
        <Typography sx={{ mt: 1.5, fontSize: '0.72rem', color: 'text.disabled', textAlign: 'center' }}>
          Signature électronique simple. Votre adresse IP, la date et le consentement sont enregistrés à des fins de preuve.
        </Typography>
      </Box>
    </Card>
  );
};

export default AgreementSignPublic;
